<!-- v1 -->

<form [formGroup]="setupForm">
  <div class="setup-intro">
    <p>
      Use this tile to build a flexible hero banner with text, imagery, actions, icons, and layout
      controls. Each section below adjusts a different part of the hero presentation.
    </p>
  </div>

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Heading 1</h2>
    </div>
    <div class="editor-section__content">
      <p class="section-intro">Controls the primary hero heading text and typography.</p>
      <lib-text-editor
        [compactColorPicker]="true"
        [fullWidth]="true"
        [formGroup]="setupForm"
        textFormControlName="heading1"
        fontColorFormControlName="heading1FontColor"
        fontSizeFormControlName="heading1FontSize"
      ></lib-text-editor>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Heading 2</h2>
    </div>
    <div class="editor-section__content">
      <p class="section-intro">Controls the secondary heading shown near the main title.</p>
      <lib-text-editor
        [compactColorPicker]="true"
        [fullWidth]="true"
        [formGroup]="setupForm"
        textFormControlName="heading2"
        fontColorFormControlName="heading2FontColor"
        fontSizeFormControlName="heading2FontSize"
      ></lib-text-editor>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Introduction</h2>
    </div>
    <div class="editor-section__content">
      <p class="section-intro">Adds the supporting introductory text for the hero banner.</p>
      <lib-text-editor
        [compactColorPicker]="true"
        [fullWidth]="true"
        [formGroup]="setupForm"
        [multiline]="true"
        textFormControlName="text"
        fontColorFormControlName="textFontColor"
        fontSizeFormControlName="textFontSize"
      ></lib-text-editor>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Image</h2>
    </div>

    <div class="editor-section__content">
      <p class="section-intro">Controls the main image shown in the hero content area.</p>
      <mat-form-field appearance="outline">
        <mat-label>Image</mat-label>
        <input matInput formControlName="contentImg" />
        <mat-hint>Defines the main image displayed in the hero banner.</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" style="margin-top:10px">
        <mat-label>Image Position</mat-label>
        <mat-select formControlName="contentImgPosition">
          <mat-option value="start">Left</mat-option>
          <mat-option value="end">Right</mat-option>
          <mat-option value="top">Top</mat-option>
          <mat-option value="bottom">Bottom</mat-option>
        </mat-select>
        <mat-hint>Controls where the main image is placed relative to the text content.</mat-hint>
      </mat-form-field>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Background Image</h2>
    </div>

    <div class="editor-section__content">
      <p class="section-intro">Controls the background image and its offsets around the hero.</p>
      <lib-image-editor
        [formGroup]="setupForm"
        imageUrlFormControlName="background"
        imageOffsetTopFormControlName="backgroundImageTopOffset"
        imageOffsetLeftFormControlName="backgroundImageLeftOffset"
        imageOffsetBottomFormControlName="backgroundImageBottomOffset"
        imageOffsetRightFormControlName="backgroundImageRightOffset"
      ></lib-image-editor>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Buttons</h2>
    </div>

    <div class="editor-section__content">
      <p class="section-intro">Add optional links or calls to action shown in the hero.</p>
      <lib-links-editor
        [compactColorPicker]="true"
        [linksFormArray]="setupForm.controls.linkButtons"
        [outlinedEntries]="true"
        [stackedPrimaryFields]="true"
        [tileInstance]="tileInstance"
        [topRightDelete]="true"
      ></lib-links-editor>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Icons</h2>
    </div>

    <div class="editor-section__content">
      <p class="section-intro">Add supporting icons and define how each one should look.</p>
      <div
        *ngFor="let icon of setupForm.controls.icons.controls; let i = index"
        [formGroup]="icon"
        class="array-entry"
      >
        <div class="array-entry-header">
          <h3>Icon {{ i + 1 }}</h3>
          <button mat-icon-button (click)="removeIcon(i)">
            <mat-icon>delete_forever</mat-icon>
          </button>
        </div>

        <mat-form-field appearance="outline">
          <mat-label>Icon</mat-label>
          <input matInput formControlName="icon" />
          <mat-hint>Sets the icon shown for this hero item.</mat-hint>
        </mat-form-field>

        <mat-form-field appearance="outline">
          <mat-label>Icon Style</mat-label>
          <mat-select formControlName="iconStyle">
            <mat-option value="basic">Basic</mat-option>
            <mat-option value="outline">Outline</mat-option>
          </mat-select>
          <mat-hint>Chooses the visual treatment applied to the icon.</mat-hint>
        </mat-form-field>

        <div class="color-fields">
          <lib-color-picker-square
            [formGroup]="icon"
            controlName="iconColor"
            [inputId]="'hero-banner-v3-icon-color-' + i"
            label="Icon Colour"
            hint="Sets the primary color of the icon."
          ></lib-color-picker-square>

          <lib-color-picker-square
            [formGroup]="icon"
            controlName="iconOutlineColor"
            [inputId]="'hero-banner-v3-icon-outline-color-' + i"
            label="Icon Outline Colour"
            hint="Sets the outline color used for the icon."
          ></lib-color-picker-square>

          <lib-color-picker-square
            [formGroup]="icon"
            controlName="iconBackgroundColor"
            [inputId]="'hero-banner-v3-icon-background-color-' + i"
            label="Icon Background Colour"
            hint="Sets the background color shown behind the icon."
          ></lib-color-picker-square>
        </div>
      </div>

      <div style="text-align: center">
        <button mat-flat-button color="primary" (click)="addIcon()">Add Icon</button>
      </div>
    </div>
  </section>
  <hr class="editor-divider" />

  <section class="editor-section">
    <div class="editor-section__header">
      <h2>Layout</h2>
    </div>

    <div class="editor-section__content">
      <p class="section-intro">These settings control the overall hero size and content alignment.</p>
      <mat-form-field appearance="outline">
        <mat-label>Min Height</mat-label>
        <input matInput formControlName="minHeight" />
        <mat-hint>Sets the minimum rendered height of the hero tile.</mat-hint>
      </mat-form-field>

      <mat-form-field appearance="outline" style="margin-top:10px">
        <mat-label>Content Alignment</mat-label>
        <mat-select formControlName="alignContent">
          <mat-option value="start">Left</mat-option>
          <mat-option value="center">Center</mat-option>
          <mat-option value="end">Right</mat-option>
        </mat-select>
        <mat-hint>Controls how the hero content is aligned within the available width.</mat-hint>
      </mat-form-field>
    </div>
  </section>
</form>

<div class="footer" style="bottom:-20px">
  <button mat-flat-button color="primary" (click)="onCreate()">Apply</button>
</div>
